import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  School,
  CheckCircle,
  Pending,
  Cancel,
  CalendarToday,
  Assignment,
  Download,
  Print,
  Share,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../services/api';
import jsPDF from 'jspdf';

// School color palette
const schoolColors = {
  primary: '#1a237e',
  secondary: '#0d47a1',
  accent: '#ff6f00',
  gold: '#ffd700',
  dark: '#0a0a2a',
  light: '#e3f2fd',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#ffffff',
};

// Styled components
const GlassCard = styled(Card)(({ theme }) => ({
  background: schoolColors.white,
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: `1px solid ${schoolColors.primary}15`,
  boxShadow: `0 8px 32px ${schoolColors.primary}15`,
}));

const SchoolButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '12px 28px',
  textTransform: 'none',
  fontWeight: 600,
  background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
  color: schoolColors.white,
  '&:hover': {
    background: `linear-gradient(135deg, ${schoolColors.secondary}, ${schoolColors.primary})`,
    transform: 'translateY(-2px)',
  },
  '&.Mui-disabled': {
    background: '#ccc',
    color: '#888',
  },
}));

const StatusCard = styled(Card)(({ theme, status }) => ({
  borderRadius: 20,
  padding: 24,
  textAlign: 'center',
  background: status === 'approved' 
    ? `linear-gradient(135deg, ${schoolColors.gold}20, ${schoolColors.gold}10)`
    : status === 'pending'
    ? `linear-gradient(135deg, ${schoolColors.accent}20, ${schoolColors.accent}10)`
    : `linear-gradient(135deg, #f4433620, #f4433610)`,
  border: `1px solid ${
    status === 'approved' ? schoolColors.gold :
    status === 'pending' ? schoolColors.accent : '#f44336'
  }30`,
}));

const TrackApplication = () => {
  const [searchReg, setSearchReg] = useState('');
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleTrack = async () => {
    if (!searchReg.trim()) {
      setError('Please enter registration number');
      return;
    }

    setLoading(true);
    setError('');
    setApplication(null);

    try {
      console.log('Searching for:', searchReg.trim());
      
      const response = await api.get(`/applications/track`, {
        params: { id: searchReg.trim() }
      });
      
      console.log('API Response:', response.data);
      
      // Store the response data in state
      setApplication(response.data);
      
    } catch (err) {
      console.error('Track error:', err);
      
      if (err.response?.status === 404) {
        setError('Application not found. Please check the registration number.');
      } else if (err.response?.status === 400) {
        setError('Invalid registration number format.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setApplication(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleTrack();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': 
        return <CheckCircle sx={{ fontSize: 80, color: schoolColors.gold }} />;
      case 'pending':  
        return <Pending sx={{ fontSize: 80, color: schoolColors.accent }} />;
      case 'rejected': 
        return <Cancel sx={{ fontSize: 80, color: '#f44336' }} />;
      case 'waiting_list':
        return <Pending sx={{ fontSize: 80, color: '#ff9800' }} />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'APPROVED';
      case 'pending':  return 'PENDING REVIEW';
      case 'rejected': return 'NOT SUCCESSFUL';
      case 'waiting_list': return 'WAITING LIST';
      default: return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'approved':
        return 'Congratulations! Your application has been approved. Please check your email for further instructions regarding reporting date and required documents.';
      case 'pending':
        return 'Your application is currently under review. We will notify you once a decision has been made.';
      case 'rejected':
        return 'We regret to inform you that your application has not been successful at this time.';
      case 'waiting_list':
        return 'Your application has been placed on the waiting list. You will be notified if a spot becomes available.';
      default:
        return '';
    }
  };

  const getStatusAlertSeverity = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending':  return 'info';
      case 'waiting_list': return 'warning';
      case 'rejected': return 'error';
      default: return 'info';
    }
  };

  const handleDownloadPDF = () => {
    if (!application) return;
    
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setTextColor(26, 35, 126);
    doc.text('Kaozya Secondary School', 105, 20, { align: 'center' });
    doc.setFontSize(16);
    doc.setTextColor(255, 111, 0);
    doc.text('Application Status Report', 105, 30, { align: 'center' });

    // Application info
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Applicant Name: ${application.name}`, 20, 45);
    doc.text(`Registration Number: ${application.registration_number}`, 20, 52);
    doc.text(`Applying For: ${application.applying_for_level?.replace('form', 'Form ')}`, 20, 59);
    doc.text(`Submission Date: ${new Date(application.submitted_date).toLocaleDateString()}`, 20, 66);
    doc.text(`Status: ${application.status.toUpperCase()}`, 20, 73);

    doc.save(`application_status_${application.registration_number}.pdf`);
    
    setSnackbar({
      open: true,
      message: 'PDF report downloaded successfully!',
      severity: 'success',
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page banner */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
          boxShadow: `0 10px 30px ${schoolColors.primary}40`,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: schoolColors.white, mb: 0.5 }}>
          Track Your Application
        </Typography>
        <Typography variant="body1" sx={{ color: schoolColors.gold }}>
          Kaozya Secondary School — Check your application status
        </Typography>
      </Box>

      {/* Search section */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Search sx={{ color: schoolColors.primary }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: schoolColors.primary }}>
              Check Application Status
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Registration Number"
                placeholder="Enter registration number (e.g., S.12345/NECTA2026)"
                value={searchReg}
                onChange={(e) => { setSearchReg(e.target.value); setError(''); }}
                onKeyDown={handleKeyDown}
                error={!!error}
                helperText={error}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: schoolColors.primary }} />,
                }}
                sx={{
                  '& label.Mui-focused': { color: schoolColors.primary },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.primary },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: schoolColors.accent },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SchoolButton
                fullWidth
                size="large"
                onClick={handleTrack}
                disabled={loading || !searchReg}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
              >
                {loading ? 'Searching...' : 'Track Application'}
              </SchoolButton>
            </Grid>
          </Grid>
        </CardContent>
      </GlassCard>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results - Application Found */}
      {application && !loading && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Student info card */}
          <GlassCard sx={{ mb: 4 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Avatar
                      sx={{
                        width: 80, height: 80, fontSize: 36, fontWeight: 700,
                        background: `linear-gradient(135deg, ${schoolColors.primary}, ${schoolColors.secondary})`,
                      }}
                    >
                      {application.name?.[0] || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: schoolColors.primary }}>
                        {application.name}
                      </Typography>
                      <Typography variant="body1" sx={{ color: schoolColors.secondary, fontFamily: 'monospace' }}>
                        {application.registration_number}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Chip
                          size="small"
                          icon={<School />}
                          label={application.applying_for_level?.replace('form', 'Form ')}
                          sx={{
                            bgcolor: `${schoolColors.primary}15`,
                            color: schoolColors.primary,
                            border: `1px solid ${schoolColors.primary}30`,
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<CalendarToday />}
                          label={`Submitted: ${new Date(application.submitted_date).toLocaleDateString()}`}
                          sx={{
                            bgcolor: `${schoolColors.accent}15`,
                            color: schoolColors.accent,
                            border: `1px solid ${schoolColors.accent}30`,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Tooltip title="Download PDF">
                      <IconButton
                        onClick={handleDownloadPDF}
                        sx={{
                          color: schoolColors.primary,
                          border: `1px solid ${schoolColors.primary}25`,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: `${schoolColors.primary}10`,
                            borderColor: schoolColors.accent,
                            color: schoolColors.accent,
                          },
                        }}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print">
                      <IconButton
                        onClick={() => window.print()}
                        sx={{
                          color: schoolColors.primary,
                          border: `1px solid ${schoolColors.primary}25`,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: `${schoolColors.primary}10`,
                            borderColor: schoolColors.accent,
                            color: schoolColors.accent,
                          },
                        }}
                      >
                        <Print />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                      <IconButton
                        onClick={() => {
                          navigator.share?.({
                            title: 'Application Status',
                            text: `Application status for ${application.name}`,
                            url: window.location.href,
                          });
                        }}
                        sx={{
                          color: schoolColors.primary,
                          border: `1px solid ${schoolColors.primary}25`,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: `${schoolColors.primary}10`,
                            borderColor: schoolColors.accent,
                            color: schoolColors.accent,
                          },
                        }}
                      >
                        <Share />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>

          {/* Status Card */}
          <StatusCard status={application.status} sx={{ mb: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              {getStatusIcon(application.status)}
              <Typography 
                variant="h3" 
                sx={{ 
                  mt: 2, 
                  fontWeight: 700,
                  color: application.status === 'approved' ? schoolColors.gold :
                         application.status === 'pending' ? schoolColors.accent : 
                         application.status === 'waiting_list' ? '#ff9800' : '#f44336',
                }}
              >
                {getStatusText(application.status)}
              </Typography>
            </Box>
          </StatusCard>

          {/* Application Details Card */}
          <GlassCard sx={{ mb: 4 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mb: 3,
                  pb: 1.5,
                  borderBottom: `2px solid ${schoolColors.primary}20`,
                }}
              >
                <Box
                  sx={{
                    width: 4, height: 24, borderRadius: 2,
                    background: `linear-gradient(180deg, ${schoolColors.primary}, ${schoolColors.accent})`,
                  }}
                />
                <Assignment sx={{ color: schoolColors.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: schoolColors.primary }}>
                  Application Details
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${schoolColors.primary}05`, border: `1px solid ${schoolColors.primary}15` }}>
                    <Typography variant="subtitle2" sx={{ color: schoolColors.primary, fontWeight: 600, mb: 1 }}>
                      Registration Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {application.registration_number}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${schoolColors.primary}05`, border: `1px solid ${schoolColors.primary}15` }}>
                    <Typography variant="subtitle2" sx={{ color: schoolColors.primary, fontWeight: 600, mb: 1 }}>
                      Applicant Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {application.name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${schoolColors.primary}05`, border: `1px solid ${schoolColors.primary}15` }}>
                    <Typography variant="subtitle2" sx={{ color: schoolColors.primary, fontWeight: 600, mb: 1 }}>
                      Applying For
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {application.applying_for_level?.replace('form', 'Form ')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${schoolColors.primary}05`, border: `1px solid ${schoolColors.primary}15` }}>
                    <Typography variant="subtitle2" sx={{ color: schoolColors.primary, fontWeight: 600, mb: 1 }}>
                      Submission Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(application.submitted_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>

          {/* Status Message Alert */}
          <GlassCard>
            <CardContent>
              <Alert 
                severity={getStatusAlertSeverity(application.status)} 
                sx={{ 
                  borderRadius: 3,
                  '& .MuiAlert-icon': { fontSize: 28 }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {getStatusMessage(application.status)}
                </Typography>
              </Alert>
            </CardContent>
          </GlassCard>
        </motion.div>
      )}

      {/* Empty state - No search yet */}
      {!application && !loading && !error && (
        <GlassCard>
          <CardContent sx={{ textAlign: 'center', py: 10 }}>
            <Box
              sx={{
                width: 90, height: 90, borderRadius: '50%', mx: 'auto', mb: 3,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${schoolColors.primary}15, ${schoolColors.accent}15)`,
                border: `2px dashed ${schoolColors.primary}30`,
              }}
            >
              <Search sx={{ fontSize: 40, color: schoolColors.primary, opacity: 0.6 }} />
            </Box>
            <Typography variant="h6" sx={{ color: schoolColors.primary, fontWeight: 600, mb: 1 }}>
              Enter your registration number to track your application
            </Typography>
            <Typography variant="body2" sx={{ color: schoolColors.secondary, opacity: 0.7 }}>
              You can find your registration number in the confirmation email or SMS
            </Typography>
          </CardContent>
        </GlassCard>
      )}

      {/* Error state */}
      {error && !loading && (
        <GlassCard>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button onClick={() => setSearchReg('')} color="primary">
              Try Again
            </Button>
          </CardContent>
        </GlassCard>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            width: '100%',
            ...(snackbar.severity === 'success' && {
              bgcolor: `${schoolColors.primary}f0`,
              color: schoolColors.white,
              '& .MuiAlert-icon': { color: schoolColors.gold },
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TrackApplication;